import React, { useState, useEffect, useRef } from 'react';
import './ConversationPanel.css';

const ConversationPanel = ({ 
  isOpen, 
  onClose, 
  onSend, 
  selectedVoice,
  isProcessing,
  translations: t 
}) => {
  const [status, setStatus] = useState('ready');
  const [isRecording, setIsRecording] = useState(false);
  const isButtonHeldRef = useRef(false);
  
  const mediaRecorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const audioChunksRef = useRef(null);
  const currentAudioRef = useRef(null);

  // Add this to track if audio has been primed
  const hasAudioBeenPrimedRef = useRef(false);

  // Function to prime audio context
  const primeAudioContext = async () => {
    try {
      // Create an audio context
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // Create a silent oscillator node
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      // Set the oscillator frequency to 440 Hz (standard A note)
      oscillator.frequency.setValueAtTime(440, audioContext.currentTime);

      // Set the gain to 0 to make it silent
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);

      // Connect oscillator to gain node and gain node to destination
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Start and stop the oscillator quickly
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.001);

      hasAudioBeenPrimedRef.current = true;
    } catch (error) {
      console.warn('Failed to prime audio context:', error);
    }
  };

  // Add effect to handle panel opening
  useEffect(() => {
    if (isOpen && !hasAudioBeenPrimedRef.current) {
      primeAudioContext();
    }
  }, [isOpen]);

  // Modify speak function to ensure audio context is primed
  const speak = async (text, voice) => {
    try {
      // Try to prime audio again if it hasn't been successful yet
      if (!hasAudioBeenPrimedRef.current) {
        await primeAudioContext();
      }

      const response = await fetch('/api/tts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          text,
          voice 
        }),
      });

      if (!response.ok) {
        throw new Error('TTS request failed');
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      currentAudioRef.current = audio;
      
      // On iOS, we need to play in response to a user interaction
      // Wrapping in a user gesture event handler
      const playAudio = () => {
        const playPromise = audio.play();
        if (playPromise) {
          playPromise.catch(error => {
            console.warn('Audio playback failed:', error);
            // If playback fails, try to prime audio again
            primeAudioContext();
          });
        }
      };

      // Try to play immediately
      playAudio();

      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        currentAudioRef.current = null;
      };

    } catch (error) {
      console.error('Error playing TTS:', error);
      throw error;
    }
  };

  // Start recording when button is pressed
  const startRecording = async (event) => {
    event.preventDefault();

    try {
      // Set button held state
      isButtonHeldRef.current = true;

      // Cancel any playing audio first
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
        // Give a small delay to ensure audio cleanup is complete
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Only proceed if button is still held
      if (!isButtonHeldRef.current) return;

      // Cancel any ongoing processing
      if (status === 'thinking' || status === 'processing' || status === 'speaking') {
        // Implement any necessary cleanup here
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(250);
      setIsRecording(true);
      setStatus('recording');
    } catch (err) {
      console.error('Error accessing microphone:', err);
      alert('Error accessing microphone. Please ensure you have granted microphone permissions.');
    }
  };

  // Stop recording when button is released
  const stopRecording = async (event) => {
    event.preventDefault();

    // Clear button held state
    isButtonHeldRef.current = false;

    if (!mediaRecorderRef.current || !isRecording) return;

    mediaRecorderRef.current.ondataavailable = (event) => {
      console.log('Final chunk size:', event.data.size);
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    mediaRecorderRef.current.onstop = async () => {
      console.log('MediaRecorder stopped, chunks:', audioChunksRef.current.length);

      if (recordingStreamRef.current) {
        recordingStreamRef.current.getTracks().forEach(track => track.stop());
        recordingStreamRef.current = null;
      }

      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      console.log('Audio blob size:', audioBlob.size);

      if (audioBlob.size === 0) {
        console.error('No audio data recorded');
        setStatus('ready');
        return;
      }

      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');

      setStatus('processing');
      
      try {
        const response = await fetch('/api/transcribe', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) throw new Error('Failed to transcribe audio');

        const { transcription } = await response.json();
        if (transcription.trim()) {
          await handleSend(transcription);
        } else {
          setStatus('ready');
        }
      } catch (error) {
        console.error('Error transcribing audio:', error);
        alert('Failed to transcribe audio.');
        setStatus('ready');
      }
    };

    mediaRecorderRef.current.stop();
    setIsRecording(false);
  };

  // Event handler for touch cancel (e.g., when touch is interrupted)
  const handleTouchCancel = (event) => {
    event.preventDefault();

    // Ensure that recording stops if touch is canceled
    if (isRecording) {
      stopRecording(event);
    }
  };

  const handleSend = async (message) => {
    try {
      setStatus('thinking');
      const response = await onSend(message);
      
      if (response?.content) {
        setStatus('speaking');
        await speak(response.content, selectedVoice);
      }
      
      setStatus('ready');
    } catch (error) {
      console.error('Error in conversation:', error);
      setStatus('ready');
    }
  };

  // Clean up when panel closes
  useEffect(() => {
    if (!isOpen) {
      isButtonHeldRef.current = false;
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      if (recordingStreamRef.current) {
        recordingStreamRef.current.getTracks().forEach(track => track.stop());
        recordingStreamRef.current = null;
      }
      setStatus('ready');
      setIsRecording(false);
    }
  }, [isOpen]);

  const getButtonText = () => {
    switch (status) {
      case 'recording': return 'Release to Send';
      case 'processing': return 'Processing Speech...';
      case 'thinking': return `${t('thinking').replace('...', '')}`; 
      case 'speaking': return `${t('speaking').replace('...', '')}`;
      default: return 'Hold to Talk';
    }
  };

  return (
    <div className={`conversation-panel ${isOpen ? 'open' : ''}`}>
      <button
        className="hold-to-talk-button"
        onMouseDown={startRecording}
        onMouseUp={stopRecording}
        onMouseLeave={stopRecording}
        onTouchStart={startRecording}
        onTouchEnd={stopRecording}
        onTouchCancel={handleTouchCancel}
        onTouchMove={(event) => event.preventDefault()} // Prevent touch scrolling
        disabled={status !== 'ready'}
      >
        {getButtonText()}
        {(status === 'thinking' || status === 'speaking') && (
          <div className="dots">
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </div>
        )}
      </button>
    </div>
  );
};

export default ConversationPanel; 